import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../App';

const PartnerManagement = () => {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [partnerSellers, setPartnerSellers] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', rev_share_percent: '', referrer_id: '', referrer_commission: '', notes: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sellerForm, setSellerForm] = useState({ name: '', email: '', commission_percent: '' });

  const fetchPartners = async () => {
    try {
      const res = await api.get('/sales-partners');
      setPartners(res.data.partners || []);
    } catch (e) {} finally { setLoading(false); }
  };

  const fetchOrgMembers = async () => {
    try { const res = await api.get('/sellers'); setOrgMembers(res.data.sellers || []); } catch (e) {}
  };

  const fetchSellers = async (partnerId) => {
    try {
      const res = await api.get(`/sales-partners/${partnerId}/sellers`);
      setPartnerSellers(prev => ({ ...prev, [partnerId]: res.data.sellers || [] }));
    } catch (e) {}
  };

  useEffect(() => { fetchPartners(); fetchOrgMembers(); }, []);

  const createPartner = async () => {
    if (!form.name || !form.rev_share_percent) return;
    try {
      await api.post('/sales-partners', form);
      setForm({ name: '', rev_share_percent: '', referrer_id: '', referrer_commission: '', notes: '' });
      setShowCreate(false);
      fetchPartners();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/sales-partners/${editingId}`, editForm);
      setEditingId(null);
      fetchPartners();
    } catch (e) { alert('Failed'); }
  };

  const deletePartner = async (id) => {
    if (!window.confirm('Delete this partner org? This cannot be undone.')) return;
    try { await api.delete(`/sales-partners/${id}`); fetchPartners(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const addSeller = async (partnerId) => {
    if (!sellerForm.name) return;
    try {
      await api.post(`/sales-partners/${partnerId}/sellers`, sellerForm);
      setSellerForm({ name: '', email: '', commission_percent: '' });
      fetchSellers(partnerId);
    } catch (e) { alert('Failed'); }
  };

  const deleteSeller = async (sellerId, partnerId) => {
    try { await api.delete(`/partner-sellers/${sellerId}`); fetchSellers(partnerId); }
    catch (e) { alert('Failed'); }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!partnerSellers[id]) fetchSellers(id);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({ name: p.name, rev_share_percent: p.rev_share_percent, referrer_id: p.referrer_id || '', referrer_commission: p.referrer_commission || '', notes: p.notes || '' });
  };

  if (loading) return <div className="p-8 text-zinc-500">Loading...</div>;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Sales Partners</h2>
          <p className="text-zinc-500 text-sm mt-1">{partners.length} partner org{partners.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-semibold">
          {showCreate ? 'Cancel' : '+ Add Partner Org'}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-zinc-900 border border-purple-800/30 rounded-xl p-5 mb-6">
          <div className="text-purple-300 text-sm font-semibold mb-3">New Partner Organization</div>
          <div className="grid grid-cols-4 gap-3 mb-3">
            <div><label className="block text-xs text-zinc-400 mb-1">Partner Name *</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="e.g. Frameplay" /></div>
            <div><label className="block text-xs text-zinc-400 mb-1">NexTide Rev Share % *</label>
              <input type="number" step="0.1" value={form.rev_share_percent} onChange={e => setForm({...form, rev_share_percent: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="30" /></div>
            <div><label className="block text-xs text-zinc-400 mb-1">Referrer (who brought them)</label>
              <select value={form.referrer_id} onChange={e => setForm({...form, referrer_id: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">None</option>
                {orgMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select></div>
            <div><label className="block text-xs text-zinc-400 mb-1">Referrer Commission %</label>
              <input type="number" step="0.5" value={form.referrer_commission} onChange={e => setForm({...form, referrer_commission: e.target.value})}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="5" /></div>
          </div>
          <div className="mb-3"><label className="block text-xs text-zinc-400 mb-1">Notes</label>
            <input type="text" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" /></div>
          <button onClick={createPartner} disabled={!form.name || !form.rev_share_percent}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">Create Partner</button>
        </div>
      )}

      {/* Partners list */}
      <div className="space-y-3">
        {partners.map(p => {
          const isExpanded = expandedId === p.id;
          const isEditing = editingId === p.id;
          const sellers = partnerSellers[p.id] || [];

          return (
            <div key={p.id} className={`bg-zinc-900 border rounded-xl overflow-hidden ${isExpanded ? 'border-purple-700/50' : 'border-zinc-800'}`}>
              {/* Partner header */}
              <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-zinc-800/30" onClick={() => toggleExpand(p.id)}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center text-purple-400 font-bold text-sm">
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-white font-medium">{p.name}</div>
                    <div className="text-zinc-500 text-xs flex gap-3">
                      <span>NexTide: <span className="text-purple-400 font-medium">{p.rev_share_percent}%</span></span>
                      <span>Partner keeps: <span className="text-zinc-300">{(100 - parseFloat(p.rev_share_percent)).toFixed(0)}%</span></span>
                      {p.referrer_name && <span>Referrer: <span className="text-yellow-400">{p.referrer_name} ({p.referrer_commission || 0}%)</span></span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-600 text-xs">{sellers.length || '...'} seller{sellers.length !== 1 ? 's' : ''}</span>
                  <span className="text-zinc-500">{isExpanded ? '▾' : '▸'}</span>
                </div>
              </div>

              {/* Expanded: edit + sellers */}
              {isExpanded && (
                <div className="border-t border-zinc-800 px-5 py-4">
                  {/* Edit partner */}
                  {isEditing ? (
                    <div className="mb-4 p-4 bg-zinc-800 rounded-lg">
                      <div className="text-sm text-zinc-400 font-medium mb-2">Edit Partner</div>
                      <div className="grid grid-cols-4 gap-3 mb-3">
                        <div><label className="block text-xs text-zinc-500 mb-1">Name</label>
                          <input type="text" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                        <div><label className="block text-xs text-zinc-500 mb-1">NexTide Rev Share %</label>
                          <input type="number" step="0.1" value={editForm.rev_share_percent} onChange={e => setEditForm({...editForm, rev_share_percent: e.target.value})}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                        <div><label className="block text-xs text-zinc-500 mb-1">Referrer</label>
                          <select value={editForm.referrer_id} onChange={e => setEditForm({...editForm, referrer_id: e.target.value})}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm">
                            <option value="">None</option>
                            {orgMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                          </select></div>
                        <div><label className="block text-xs text-zinc-500 mb-1">Referrer Commission %</label>
                          <input type="number" step="0.5" value={editForm.referrer_commission} onChange={e => setEditForm({...editForm, referrer_commission: e.target.value})}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="bg-purple-600 text-white px-3 py-1.5 rounded text-sm font-semibold">Save</button>
                        <button onClick={() => setEditingId(null)} className="bg-zinc-700 text-white px-3 py-1.5 rounded text-sm">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 mb-4">
                      <button onClick={() => startEdit(p)} className="text-xs text-zinc-500 hover:text-purple-400">Edit Partner</button>
                      <button onClick={() => deletePartner(p.id)} className="text-xs text-zinc-600 hover:text-red-400">Delete Partner</button>
                    </div>
                  )}

                  {/* Sellers */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs text-zinc-500 uppercase tracking-wide font-semibold">Sellers at {p.name}</div>
                    </div>

                    {sellers.length > 0 && (
                      <div className="space-y-1 mb-3">
                        {sellers.map(s => (
                          <div key={s.id} className="flex items-center justify-between bg-zinc-800 rounded-lg px-4 py-2">
                            <div>
                              <span className="text-white text-sm">{s.name}</span>
                              {s.email && <span className="text-zinc-500 text-xs ml-2">{s.email}</span>}
                              {s.commission_percent > 0 && <span className="text-yellow-400 text-xs ml-2">{s.commission_percent}% commission</span>}
                            </div>
                            <button onClick={() => deleteSeller(s.id, p.id)} className="text-zinc-600 hover:text-red-400 text-xs">Remove</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add seller form */}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1"><label className="block text-xs text-zinc-500 mb-1">Name *</label>
                        <input type="text" value={sellerForm.name} onChange={e => setSellerForm({...sellerForm, name: e.target.value})}
                          placeholder="Seller name..." className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-white text-sm" /></div>
                      <div className="flex-1"><label className="block text-xs text-zinc-500 mb-1">Email</label>
                        <input type="email" value={sellerForm.email} onChange={e => setSellerForm({...sellerForm, email: e.target.value})}
                          placeholder="Optional" className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-white text-sm" /></div>
                      <div className="w-24"><label className="block text-xs text-zinc-500 mb-1">Comm. %</label>
                        <input type="number" step="0.5" value={sellerForm.commission_percent} onChange={e => setSellerForm({...sellerForm, commission_percent: e.target.value})}
                          placeholder="0" className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-white text-sm" /></div>
                      <button onClick={() => addSeller(p.id)} disabled={!sellerForm.name}
                        className="bg-purple-600 text-white px-3 py-1.5 rounded text-sm font-semibold disabled:opacity-50 shrink-0">+ Add</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {partners.length === 0 && !showCreate && (
          <div className="text-center py-16">
            <div className="text-zinc-500 text-lg mb-2">No partner organizations yet</div>
            <div className="text-zinc-600 text-sm">Add your first sales partner to start tracking partner deals</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerManagement;
